<template>
  <v-container>
    <v-card class="elevation-4">
      <v-card-title class="primary white--text">
        <v-icon left dark>mdi-account-cash</v-icon>
        Manage User Credits
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model="search"
          prepend-icon="mdi-magnify"
          label="Search by username, email, or name..."
          clearable
          @input="debouncedSearch"
          :loading="searchLoading"
        ></v-text-field>

        <v-chip small color="info" class="mt-2">
          <v-icon left small>mdi-account-multiple</v-icon>
          {{ users.length }} user(s) found
        </v-chip>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="users"
        :loading="loading"
        class="elevation-1"
      >
        <template v-slot:item.full_name="{ item }">
          <div>
            <div class="font-weight-bold">{{ item.full_name }}</div>
            <div class="caption grey--text">@{{ item.username }}</div>
            <div class="caption grey--text">{{ item.email }}</div>
          </div>
        </template>

        <template v-slot:item.total_points="{ item }">
          <v-chip :color="item.total_points > 0 ? 'success' : 'grey'" dark small>
            {{ formatNumber(item.total_points) }} pts
          </v-chip>
        </template>

        <template v-slot:item.last_transaction="{ item }">
          <span v-if="item.last_transaction" class="caption">
            {{ formatDate(item.last_transaction) }}
          </span>
          <span v-else class="grey--text caption">No transactions</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn small color="primary" @click="openAddPointsDialog(item)" class="mr-2">
            <v-icon small left>mdi-plus</v-icon>
            Add
          </v-btn>
          <v-btn small outlined color="info" @click="viewHistory(item)">
            <v-icon small left>mdi-history</v-icon>
            History
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialog ajouter points -->
    <v-dialog v-model="addPointsDialog" max-width="500px">
      <v-card>
        <v-card-title class="primary white--text">
          Add Points to {{ selectedUser ? selectedUser.full_name : '' }}
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form ref="form" v-model="formValid">
            <v-text-field
              v-model="pointsForm.points"
              label="Points Amount"
              type="number"
              :rules="[rules.required, rules.number]"
              prepend-icon="mdi-currency-usd"
            ></v-text-field>

            <v-select
              v-model="pointsForm.credit_type"
              :items="creditTypes"
              item-text="name"
              item-value="id"
              label="Transaction Type"
              prepend-icon="mdi-tag"
              class="mt-3"
            ></v-select>

            <v-textarea
              v-model="pointsForm.reason"
              label="Reason (optional)"
              prepend-icon="mdi-note-text"
              rows="3"
              class="mt-3"
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addPointsDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!formValid" :loading="submitting" @click="submitPoints">
            Add Points
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog historique -->
    <v-dialog v-model="historyDialog" max-width="700px">
      <v-card>
        <v-card-title class="info white--text">
          <div>
            <div>Credit History</div>
            <div class="caption">{{ historyUser ? historyUser.username : '' }}</div>
          </div>
          <v-spacer></v-spacer>
          <v-chip dark>Total: {{ formatNumber(historyTotal) }} pts</v-chip>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-list v-if="history.length > 0" two-line>
            <v-list-item v-for="item in history" :key="item.id">
              <v-list-item-avatar>
                <v-icon :color="item.points > 0 ? 'success' : 'error'">
                  {{ item.points > 0 ? 'mdi-plus-circle' : 'mdi-minus-circle' }}
                </v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title>
                  <span :class="item.points > 0 ? 'success--text' : 'error--text'">
                    {{ item.points > 0 ? '+' : '' }}{{ item.points }} points
                  </span>
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.credit_level }} • {{ item.date_display }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-else class="pa-5 text-center grey--text">No transactions yet</div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="historyDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ManageUserCredits',

  data() {
    return {
      loading: false,
      searchLoading: false,
      users: [],
      search: '',
      searchTimeout: null,
      addPointsDialog: false,
      historyDialog: false,
      selectedUser: null,
      historyUser: null,
      history: [],
      historyTotal: 0,
      currentLevel: null,
      creditTypes: [],
      formValid: false,
      submitting: false,

      pointsForm: {
        points: null,
        credit_type: 'manual',
        reason: ''
      },

      rules: {
        required: v => !!v || 'Required',
        number: v => !isNaN(v) || 'Must be a number'
      },

      headers: [
        { text: 'User', value: 'full_name', sortable: true },
        { text: 'Total Points', value: 'total_points', sortable: true, align: 'center' },
        { text: 'Last Transaction', value: 'last_transaction', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' }
      ],

      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },

  mounted() {
    this.loadUsers()
    this.loadCreditTypes()
  },

  methods: {
    async loadUsers() {
      this.loading = true
      try {
        const params = {}
        if (this.search && this.search.trim()) {
          params.search = this.search.trim()
        }

        const response = await axios.get('/api/v1/manager/credits/manage/', { params })
        this.users = response.data.users
      } catch (error) {
        this.showError('Failed to load users')
        console.error(error)
      } finally {
        this.loading = false
        this.searchLoading = false
      }
    },

    debouncedSearch() {
      this.searchLoading = true
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => this.loadUsers(), 500)
    },

    async loadCreditTypes() {
      try {
        const response = await axios.get('/api/v1/manager/credits/types/')
        this.creditTypes = response.data.credit_types
      } catch (error) {
        console.error('Failed to load credit types:', error)
      }
    },

    openAddPointsDialog(user) {
      this.selectedUser = user
      this.pointsForm = { points: null, credit_type: 'manual', reason: '' }
      this.addPointsDialog = true
    },

    async submitPoints() {
      if (!this.$refs.form.validate()) return

      this.submitting = true
      try {
        const response = await axios.post('/api/v1/manager/credits/manage/', {
          user_id: this.selectedUser.id,
          points: parseInt(this.pointsForm.points),
          credit_type: this.pointsForm.credit_type,
          reason: this.pointsForm.reason
        })

        this.showSuccess(response.data.message)
        this.addPointsDialog = false
        this.loadUsers()
      } catch (error) {
        this.showError(error.response?.data?.error || 'Failed to add points')
      } finally {
        this.submitting = false
      }
    },

    async viewHistory(user) {
      this.historyUser = user
      this.historyDialog = true

      try {
        const response = await axios.get(`/api/v1/manager/credits/history/${user.id}/`)
        this.history = response.data.history
        this.historyTotal = response.data.total_points
        this.currentLevel = response.data.current_level
      } catch (error) {
        this.showError('Failed to load history')
        console.error(error)
      }
    },

    formatNumber(num) {
      return new Intl.NumberFormat('fr-FR').format(num || 0)
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    showSuccess(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'success'
      this.snackbar = true
    },

    showError(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'error'
      this.snackbar = true
    }
  }
}
</script>
